import { Layout as AppLayout } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  type BlockerFunction,
  Outlet,
  useBlocker,
  useLocation,
  useNavigation,
} from 'react-router-dom';

import ForcePasswordChangeManager from '../../foundation/components/force_password_change/ForcePasswordChangeManager';
import FullPageLoader from '../../foundation/components/full_page_loader/FullPageLoader';
import LoginManager from '../../foundation/components/login/LoginManager';
import { useSelectedRoute } from '../../foundation/config/useSelectedRoute';
import { selectRequiresPasswordChange } from '../authentication/redux/selectors';
import RunningTaskMonitor from '../running_tasks/RunningTaskMonitor';
import { InterpretationSnapshotCapture } from '../variant_workflow/interpretation';
import Footer from './footer/Footer';
import Header from './header/Header';
import Sider from './sider/Sider';

const { Content } = AppLayout;

/**
 * Layout
 *
 * Shell of the application. It renders the common Header, Sider, Footer and the
 * current page's <Outlet />.
 *
 * Data-router responsibilities:
 * - `useNavigation` drives the global pending UI, replacing the per-tab
 *   isLoading aggregations in the workflow components.
 *   Quelle: https://reactrouter.com/api/hooks/useNavigation
 * - `useBlocker` "allow[s] the application to block navigations within the
 *   SPA" - closes the URL-bypass described in DNAWZ-545.
 *   Quelle: https://reactrouter.com/api/hooks/useBlocker
 */
const Layout = () => {
  const selectedRoute = useSelectedRoute();
  const location = useLocation();
  const navigation = useNavigation();
  const requiresPasswordChange = useSelector(selectRequiresPasswordChange);

  const isWorkFlowStage = location.pathname.includes('variant-workflow');
  const isLegalPage = location.pathname.includes('/legal');
  const isInterpretationSnapshot =
    isWorkFlowStage &&
    new URLSearchParams(location.search).has('interpretation_snapshot');

  // One loader for the whole route while the workflow loader runs; other
  // navigations (e.g. requireAuth) finish too fast to warrant an overlay.
  const isRouteLoading =
    navigation.state === 'loading' &&
    navigation.location?.pathname?.includes('variant-workflow');

  // Block navigation while the forced password change modal is active.
  // Only block when the target pathname actually changes, matching the
  // official function-form example.
  // Quelle: https://reactrouter.com/api/hooks/useBlocker
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      requiresPasswordChange &&
      currentLocation.pathname !== nextLocation.pathname,
    [requiresPasswordChange],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      // "blocker.reset() ... return[s] the blocker to an unblocked state and
      // leave[s] the user at the current location." The modal is already
      // shown by ForcePasswordChangeManager.
      // Quelle: https://reactrouter.com/api/hooks/useBlocker
      blocker.reset();
    }
  }, [blocker.state, blocker.reset]);

  if (!selectedRoute?.hasLayout) {
    return (
      <>
        <Outlet />
        {/* Global managers must also be present on pages without a full layout (e.g. the landing page) */}
        <ForcePasswordChangeManager />
      </>
    );
  }

  return (
    <AppLayout className="layout">
      <Header hideUserProfile={isInterpretationSnapshot} />
      <RunningTaskMonitor />
      <AppLayout>
        {!isInterpretationSnapshot && <Sider />}
        <Content
          className={`layout__content ${
            isWorkFlowStage || isLegalPage ? 'layout__content--no-scroll' : ''
          }`}
        >
          {isRouteLoading ? <FullPageLoader /> : <Outlet />}
        </Content>
      </AppLayout>
      {isWorkFlowStage && !isInterpretationSnapshot && (
        <InterpretationSnapshotCapture />
      )}
      {/* Global login modal manager mounted at root to listen for unauthorized events */}
      <LoginManager />
      {/* Forced password change prompt, shown only after a fresh active login */}
      <ForcePasswordChangeManager />
      {/* Version badge – visible only in non-production environments */}
      <Footer />
    </AppLayout>
  );
};

export default Layout;
